import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LayoutDashboard, CalendarRange, History, Percent } from "lucide-react";
import { useCabangOptions } from "../../lib/cabang";
import { useAuth } from "../../context/AuthContext";
import {
  DashboardPriceProtection, LaporanPeriode, DaftarProgramPanel, ProgramDetailModal, FormProgram,
  useProgramDiUrl, RUTE_PROGRAM_LAMA,
} from "./CashbackProgram";

/** Dashboard Program Price Protection — item "Dashboard" di submenu (keputusan
 *  A8, brief owner 22 Sep 2026). Klik parent menu hanya membuka/menutup
 *  submenu dan halaman Support Aktivasi tidak punya tombol Dashboard, jadi
 *  dashboard butuh halamannya sendiri.
 *
 *  Tiga tampilan (`?view=`):
 *   - ringkasan : total program SELESAI per jenis & kanal + yang sudah cair,
 *                 lalu pipeline klaim semua program;
 *   - laporan   : Laporan Periode lintas program (tujuan Pusat Laporan);
 *   - lama      : program yang dibuat SEBELUM brief ini (tanpa jenis/kanal) —
 *                 tidak punya salah satu dari empat halaman, jadi tinggal di
 *                 sini supaya tetap bisa dikelola & diklaim. Tab-nya hanya
 *                 muncul kalau program lama memang ada (toko white-label baru
 *                 tidak pernah punya).
 *  Rute lama `/finance/cashback/programs` dialihkan ke sini beserta query
 *  string-nya (App.js). */
export default function PriceProtectionDashboard() {
  const navigate = useNavigate();
  const { branding } = useAuth();
  const namaToko = branding?.nama_toko_sendiri || "EppleHub";
  const cabangOptions = useCabangOptions({ denganKosong: true });
  const [sp, setSp] = useSearchParams();
  const view = ["ringkasan", "laporan", "lama"].includes(sp.get("view")) ? sp.get("view") : "ringkasan";
  const [adaLama, setAdaLama] = useState(false);
  const [reload, setReload] = useState(0);
  const bump = () => setReload((n) => n + 1);
  const [editing, setEditing] = useState(null);
  // Detail program lama dibuka lewat `?program=` di tab "Program Lama". Program
  // ber-jenis yang tautannya mendarat di sini dialihkan ke halamannya sendiri.
  const pd = useProgramDiUrl(RUTE_PROGRAM_LAMA);

  const gantiView = (v) => setSp((prev) => {
    const n = new URLSearchParams(prev); n.set("view", v); n.delete("program"); return n;
  }, { replace: true });

  const tombol = [
    { key: "ringkasan", label: "Ringkasan", icon: LayoutDashboard, tid: "cb-ppdash-view-ringkasan" },
    { key: "laporan", label: "Laporan Periode", icon: CalendarRange, tid: "cb-ppdash-view-laporan" },
    (adaLama || view === "lama") && { key: "lama", label: "Program Lama", icon: History, tid: "cb-ppdash-view-lama" },
  ].filter(Boolean);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-price-protection-dashboard">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Program Price Protection</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Percent className="w-7 h-7 text-emerald-600" /> Dashboard
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Total Support Aktivasi & Support Cashback ({namaToko} dan Dealer Partner) dari program yang sudah disetujui Selesai, berdampingan dengan yang sudah cair dari supplier.
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-1 inline-flex flex-wrap">
          {tombol.map((t) => (
            <button key={t.key} onClick={() => gantiView(t.key)} data-testid={t.tid}
                    className={`h-8 px-3 rounded-md text-xs font-medium inline-flex items-center gap-1.5 whitespace-nowrap transition ${view === t.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {view === "laporan" ? (
        <LaporanPeriode />
      ) : view === "lama" ? (
        <>
          <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
            Program yang dibuat sebelum 22 Sep 2026 — tanpa jenis & kanal. Cashback-nya tetap dihitung dengan rumus lama
            (modal asli unit − harga baru). Program baru dibuat dari submenu Program Support Aktivasi / Program Support Cashback.
          </p>
          <DaftarProgramPanel jenis="lama" reloadKey={reload} onOpen={pd.buka} />
        </>
      ) : (
        <DashboardPriceProtection reloadKey={reload}
                                  onData={(d) => setAdaLama((d?.price_protection?.per_jenis_kanal || []).some((r) => r.kanal === "semua"))} />
      )}

      {editing && (
        <FormProgram program={editing} namaToko={namaToko} cabangOptions={cabangOptions}
                         onClose={() => setEditing(null)}
                         onCreated={() => { setEditing(null); bump(); pd.segarkan(); }} />
      )}
      {pd.program && (
        <ProgramDetailModal program={pd.program} onClose={pd.tutup}
                            onChanged={async () => { bump(); await pd.segarkan(); }}
                            onDeleted={() => { pd.tutup(); bump(); }}
                            onEdit={() => setEditing(pd.program)}
                            navigate={navigate} />
      )}
    </div>
  );
}
